import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import LoginPage from '../pages/Auth/LoginPage';
import SignupPage from '../pages/Auth/SignupPage';
import DashboardPage from '../pages/Dashboard/DashboardPage';
import ClassesPage from '../pages/Classes/ClassesPage';
import ClassPage from '../pages/Class/ClassPage';
import SessionPage from '../pages/Session/SessionPage';
import DeckViewPage from '../pages/Flashcards/DeckViewPage';
import StudyModePage from '../pages/Study/StudyModePage';
import StudyGroupsPage from '../pages/Study/StudyGroupsPage';
import ProfilePage from '../pages/Profile/ProfilePage';
import NotesPage from '../pages/Notes/NotesPage';
import MyFlashcardsPage from '../pages/Flashcards/MyFlashcardsPage';
import ProfileViewPage from '../pages/Profile/ProfileViewPage';
import NoteViewPage from '../pages/Notes/NoteViewPage';
import AboutPage from '../pages/About/AboutPage';

export const routes = (
  <Routes>
    <Route
      path="/login"
      element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      }
    />
    <Route
      path="/about"
      element={
        <GuestRoute>
          <AboutPage />
        </GuestRoute>
      }
    />
    <Route
      path="/study-groups"
      element={
        <ProtectedRoute>
          <StudyGroupsPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/signup"
      element={
        <PublicRoute>
          <SignupPage />
        </PublicRoute>
      }
    />
    <Route
      path="/dashboard"
      element={
        <GuestRoute>
          <DashboardPage />
        </GuestRoute>
      }
    />
    <Route
      path="/classes"
      element={
        <GuestRoute>
          <ClassesPage />
        </GuestRoute>
      }
    />
    <Route
      path="/class/:classId"
      element={
        <GuestRoute>
          <ClassPage />
        </GuestRoute>
      }
    />
    <Route
      path="/note/:noteId"
      element={
        <ProtectedRoute>
          <NoteViewPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/session/:sessionId"
      element={
        <ProtectedRoute>
          <SessionPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/deck/:deckId"
      element={
        <ProtectedRoute>
          <DeckViewPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/study/:deckId"
      element={
        <ProtectedRoute>
          <StudyModePage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/profile"
      element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/user/:userId"
      element={
        <ProtectedRoute>
          <ProfileViewPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/notes"
      element={
        <ProtectedRoute>
          <NotesPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/flashcards"
      element={
        <ProtectedRoute>
          <MyFlashcardsPage />
        </ProtectedRoute>
      }
    />
    <Route 
      path="/" 
      element={
        <GuestRoute>
          <Navigate to="/dashboard" replace />
        </GuestRoute>
      } 
    />
  </Routes>
);

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-primary-700 border-t-transparent"></div>
          <p className="mt-4 text-gray-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function GuestRoute({ children }: { children: React.ReactNode }) {
  const { loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-primary-700 border-t-transparent"></div>
          <p className="mt-4 text-gray-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  // Allow both guests and authenticated users
  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-primary-700 border-t-transparent"></div>
          <p className="mt-4 text-gray-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

